
// src/pages/InvoicePayment.js
import React, { useState, useEffect, Suspense } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Alert, AlertDescription } from '../components/ui/alert';
import { AlertCircle, CheckCircle } from 'lucide-react';
import Lottie from 'lottie-react';
import successAnimation from '../assets/success-animation.json';
import { useConfig } from '../context/ConfigContext';

// Lazy-load the payment form component
const PaymentForm = React.lazy(() => import('../components/InvoicePaymentForm'));

const InvoicePayment = () => {
  const { paymentId } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const { config } = useConfig(); // Get config colors

  // Fetch Stripe public key
  useEffect(() => {
    const fetchStripeKey = async () => {
      try {
        const { data } = await axios.get('/api/config/stripe');
        if (data.publicKey) {
          const promise = await loadStripe(data.publicKey);
          setStripePromise(promise);
        } else {
          throw new Error('No public key in response');
        }
      } catch (err) {
        console.error("Error fetching Stripe public key:", err);
        setError("Could not load payment system. Please try again later.");
      }
    };
    
    fetchStripeKey();
  }, []);

  // Fetch invoice data
  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        setLoading(true);
        const { data } = await axios.get(`/api/invoices/payment/${paymentId}`);
        setInvoice(data);
        setError(null);
      } catch (err) {
        console.error("Error fetching invoice:", err);
        setError(err.response?.data?.error || "Invoice not found or has expired.");
      } finally {
        setLoading(false);
      }
    };

    if (paymentId) {
      fetchInvoice();
    }
  }, [paymentId]);

  const handlePaymentSuccess = (result) => {
    setPaymentSuccess(true);
    setOrderId(result.orderId);
    // Redirect to success page after a delay
    setTimeout(() => {
      navigate(`/invoice-success?orderId=${result.orderId}`);
    }, 3000);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: config.colors.primary }}></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6 mt-10">
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-5 w-5" />
          <AlertDescription className="ml-2 text-lg">{error}</AlertDescription>
        </Alert>
        <div className="text-center mt-8">
          <p>If you believe this is an error, please contact our support team.</p>
          <a href="/" className="inline-block mt-4 px-6 py-3 text-white rounded-md transition-colors" 
            style={{ backgroundColor: config.colors.primary, ':hover': { backgroundColor: `${config.colors.primary}DD` } }}>
            Return to Home
          </a>
        </div>
      </div>
    );
  }

  if (paymentSuccess) {
    return (
      <div className="max-w-4xl mx-auto p-6 mt-10 text-center">
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="flex justify-center mb-4">
            <Lottie 
              animationData={successAnimation} 
              loop={false} 
              style={{ width: 150, height: 150 }} 
            />
          </div>
          <h2 className="text-2xl font-bold text-green-600 mb-4">Payment Successful!</h2>
          <p className="text-lg mb-4">
            Thank you for your payment. Your order has been created and will be processed shortly.
          </p>
          <div className="flex items-center justify-center text-green-600 mb-4">
            <CheckCircle className="h-6 w-6 mr-2" />
            <span>Invoice #{invoice.invoiceNumber} marked as paid</span>
          </div>
          <a 
            // href={`/order-details/${orderId}`} 
            href={`/orders`} 
            className="mt-6 inline-block px-6 py-3text-white rounded-md  transition-colors"
            style={{ backgroundColor: config.colors.primary }}
          >
            View Order Details
          </a>
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="max-w-4xl mx-auto p-6 mt-10">
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-5 w-5" />
          <AlertDescription className="ml-2 text-lg">Invoice not found</AlertDescription>
        </Alert>
      </div>
    );
  }

  // Invoice found, display it with payment form
  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 lg:p-8 py-8 bg-gray-50">
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-6 text-white" style={{ backgroundColor: config.colors.primary }}>
          <h1 className="text-2xl font-bold">Invoice #{invoice.invoiceNumber}</h1>
          <div className="flex justify-between items-start mt-2">
            <div>
              <p>Issue Date: {new Date(invoice.issueDate).toLocaleDateString()}</p>
              <p>Due Date: {new Date(invoice.dueDate).toLocaleDateString()}</p>
            </div>
            <div className="text-right">
              <p className="text-xl font-bold">Total: ${invoice.totalAmount.toFixed(2)}</p>
              <p className="text-sm">Status: {invoice.status}</p>
            </div>
          </div>
        </div>

        {/* Client Info */}
        <div className="p-6 border-b">
          <h2 className="text-lg font-semibold mb-2">Bill To:</h2>
          <p>{invoice.client.firstName} {invoice.client.lastName}</p>
          <p>{invoice.client.email}</p>
        </div>

        {/* Invoice Items */}
        <div className="p-6">
          <h2 className="text-lg font-semibold mb-4">Invoice Items</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Quantity
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Unit Price
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invoice.items.map((item, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.product.name}
                      {item.description && <p className="text-gray-500 text-xs mt-1">{item.description}</p>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.quantity}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      ${item.unitPrice.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      ${item.totalPrice.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Summary */}
          <div className="mt-8 border-t pt-4">
            <div className="flex justify-end">
              <div className="w-64">
                <div className="flex justify-between py-2">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-medium">${invoice.subtotal.toFixed(2)}</span>
                </div>
                {invoice.gstTotal > 0 && (
                  <div className="flex justify-between py-2">
                    <span className="text-gray-600">GST (5%):</span>
                    <span>${invoice.gstTotal.toFixed(2)}</span>
                  </div>
                )}
                {invoice.pstTotal > 0 && (
                  <div className="flex justify-between py-2">
                    <span className="text-gray-600">PST (7%):</span>
                    <span>${invoice.pstTotal.toFixed(2)}</span>
                  </div>
                )}
                {invoice.discountAmount > 0 && (
                  <div className="flex justify-between py-2 text-green-600">
                    <span>Discount:</span>
                    <span>-${invoice.discountAmount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between py-2 border-t border-gray-300 font-bold">
                  <span>Total:</span>
                  <span>${invoice.totalAmount.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Section */}
        <div className="p-6 bg-gray-50 border-t">
          <h2 className="text-lg font-semibold mb-4">Payment</h2>
          
          {invoice.status === 'paid' ? (
            <div className="flex items-center text-green-600">
              <CheckCircle className="h-5 w-5 mr-2" />
              <span>This invoice has been paid. Thank you!</span>
            </div>
          ) : (
            <>
              {stripePromise ? (
                <Suspense fallback={
                  <div className="flex items-center justify-center p-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    <span className="ml-2">Loading payment form...</span>
                  </div>
                }>
                  <Elements stripe={stripePromise}>
                    <PaymentForm 
                      invoice={invoice} 
                      paymentId={paymentId}
                      onPaymentSuccess={handlePaymentSuccess}
                    />
                  </Elements>
                </Suspense>
              ) : (
                <div className="flex justify-center items-center p-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                  <span className="ml-2">Loading payment system...</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvoicePayment;









// // before config coloring 
// // src/pages/InvoicePayment.js
// import React, { useState, useEffect, Suspense } from 'react';
// import { useParams, useNavigate } from 'react-router-dom';
// import axios from 'axios';
// import { Elements } from '@stripe/react-stripe-js';
// import { loadStripe } from '@stripe/stripe-js';
// import { Alert, AlertDescription } from '../components/ui/alert';
// import { AlertCircle, CheckCircle } from 'lucide-react';
// import Lottie from 'lottie-react';
// import successAnimation from '../assets/success-animation.json';

// // Lazy-load the payment form component
// const PaymentForm = React.lazy(() => import('../components/InvoicePaymentForm'));

// const InvoicePayment = () => {
//   const { paymentId } = useParams();
//   const navigate = useNavigate();
//   const [invoice, setInvoice] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [stripePromise, setStripePromise] = useState(null);
//   const [paymentSuccess, setPaymentSuccess] = useState(false);
//   const [orderId, setOrderId] = useState(null);

//   // Fetch Stripe public key
//   useEffect(() => {
//     const fetchStripeKey = async () => {
//       try {
//         const { data } = await axios.get('/api/config/stripe');
//         if (data.publicKey) {
//           const promise = await loadStripe(data.publicKey);
//           setStripePromise(promise);
//         } else {
//           throw new Error('No public key in response');
//         }
//       } catch (err) {
//         console.error("Error fetching Stripe public key:", err);
//         setError("Could not load payment system. Please try again later.");
//       }
//     };
    
//     fetchStripeKey();
//   }, []);

//   // Fetch invoice data
//   useEffect(() => {
//     const fetchInvoice = async () => {
//       try {
//         setLoading(true);
//         const { data } = await axios.get(`/api/invoices/payment/${paymentId}`);
//         setInvoice(data);
//         setError(null);
//       } catch (err) {
//         console.error("Error fetching invoice:", err);
//         setError(err.response?.data?.error || "Invoice not found or has expired.");
//       } finally {
//         setLoading(false);
//       }
//     };

//     if (paymentId) {
//       fetchInvoice();
//     }
//   }, [paymentId]);

//   const handlePaymentSuccess = (result) => {
//     setPaymentSuccess(true);
//     setOrderId(result.orderId);
//     // Redirect to success page after a delay
//     setTimeout(() => {
//       navigate(`/invoice-success?orderId=${result.orderId}`);
//     }, 3000);
//   };

//   if (loading) {
//     return (
//       <div className="flex justify-center items-center min-h-screen bg-gray-50">
//         <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div className="max-w-4xl mx-auto p-6 mt-10">
//         <Alert variant="destructive" className="mb-4">
//           <AlertCircle className="h-5 w-5" />
//           <AlertDescription className="ml-2 text-lg">{error}</AlertDescription>
//         </Alert>
//         <div className="text-center mt-8">
//           <p>If you believe this is an error, please contact our support team.</p>
//           <a href="/" className="inline-block mt-4 px-6 py-3 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors">
//             Return to Home
//           </a>
//         </div>
//       </div>
//     );
//   }

//   if (paymentSuccess) {
//     return (
//       <div className="max-w-4xl mx-auto p-6 mt-10 text-center">
//         <div className="bg-white rounded-lg shadow-md p-8">
//           <div className="flex justify-center mb-4">
//             <Lottie 
//               animationData={successAnimation} 
//               loop={false} 
//               style={{ width: 150, height: 150 }} 
//             />
//           </div>
//           <h2 className="text-2xl font-bold text-green-600 mb-4">Payment Successful!</h2>
//           <p className="text-lg mb-4">
//             Thank you for your payment. Your order has been created and will be processed shortly.
//           </p>
//           <div className="flex items-center justify-center text-green-600 mb-4">
//             <CheckCircle className="h-6 w-6 mr-2" />
//             <span>Invoice #{invoice.invoiceNumber} marked as paid</span>
//           </div>
//           <a 
//             href={`/order-details/${orderId}`} 
//             className="mt-6 inline-block px-6 py-3 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
//           >
//             View Order Details
//           </a>
//         </div>
//       </div>
//     );
//   }

//   if (!invoice) {
//     return (
//       <div className="max-w-4xl mx-auto p-6 mt-10">
//         <Alert variant="destructive" className="mb-4">
//           <AlertCircle className="h-5 w-5" />
//           <AlertDescription className="ml-2 text-lg">Invoice not found</AlertDescription>
//         </Alert>
//       </div>
//     );
//   }

//   // Invoice found, display it with payment form
//   return (
//     <div className="max-w-5xl mx-auto p-4 sm:p-6 lg:p-8 py-8 bg-gray-50">
//       <div className="bg-white rounded-lg shadow-md overflow-hidden">
//         <div className="p-6 bg-blue-500 text-white">
//           <h1 className="text-2xl font-bold">Invoice #{invoice.invoiceNumber}</h1>
//           <div className="flex justify-between items-start mt-2">
//             <div>
//               <p>Issue Date: {new Date(invoice.issueDate).toLocaleDateString()}</p>
//               <p>Due Date: {new Date(invoice.dueDate).toLocaleDateString()}</p>
//             </div>
//             <div className="text-right">
//               <p className="text-xl font-bold">Total: ${invoice.totalAmount.toFixed(2)}</p>
//               <p className="text-sm">Status: {invoice.status}</p>
//             </div>
//           </div>
//         </div>

//         {/* Client Info */}
//         <div className="p-6 border-b">
//           <h2 className="text-lg font-semibold mb-2">Bill To:</h2>
//           <p>{invoice.client.firstName} {invoice.client.lastName}</p>
//           <p>{invoice.client.email}</p>
//         </div>

//         {/* Invoice Items */}
//         <div className="p-6">
//           <h2 className="text-lg font-semibold mb-4">Invoice Items</h2>
//           <div className="overflow-x-auto">
//             <table className="min-w-full divide-y divide-gray-200">
//               <thead className="bg-gray-50">
//                 <tr>
//                   <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
//                     Description
//                   </th>
//                   <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
//                     Quantity
//                   </th>
//                   <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
//                     Unit Price
//                   </th>
//                   <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
//                     Total
//                   </th>
//                 </tr>
//               </thead>
//               <tbody className="bg-white divide-y divide-gray-200">
//                 {invoice.items.map((item, index) => (
//                   <tr key={index}>
//                     <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
//                       {item.product.name}
//                       {item.description && <p className="text-gray-500 text-xs mt-1">{item.description}</p>}
//                     </td>
//                     <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
//                       {item.quantity}
//                     </td>
//                     <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
//                       ${item.unitPrice.toFixed(2)}
//                     </td>
//                     <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
//                       ${item.totalPrice.toFixed(2)}
//                     </td>
//                   </tr>
//                 ))}
//               </tbody>
//             </table>
//           </div>

//           {/* Summary */}
//           <div className="mt-8 border-t pt-4">
//             <div className="flex justify-end">
//               <div className="w-64">
//                 <div className="flex justify-between py-2">
//                   <span className="text-gray-600">Subtotal:</span>
//                   <span className="font-medium">${invoice.subtotal.toFixed(2)}</span>
//                 </div>
//                 {invoice.gstTotal > 0 && (
//                   <div className="flex justify-between py-2">
//                     <span className="text-gray-600">GST (5%):</span>
//                     <span>${invoice.gstTotal.toFixed(2)}</span>
//                   </div>
//                 )}
//                 {invoice.pstTotal > 0 && (
//                   <div className="flex justify-between py-2">
//                     <span className="text-gray-600">PST (7%):</span>
//                     <span>${invoice.pstTotal.toFixed(2)}</span>
//                   </div>
//                 )}
//                 {invoice.discountAmount > 0 && (
//                   <div className="flex justify-between py-2 text-green-600">
//                     <span>Discount:</span>
//                     <span>-${invoice.discountAmount.toFixed(2)}</span>
//                   </div>
//                 )}
//                 <div className="flex justify-between py-2 border-t border-gray-300 font-bold">
//                   <span>Total:</span>
//                   <span>${invoice.totalAmount.toFixed(2)}</span>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>

//         {/* Payment Section */}
//         <div className="p-6 bg-gray-50 border-t">
//           <h2 className="text-lg font-semibold mb-4">Payment</h2>
          
//           {invoice.status === 'paid' ? (
//             <div className="flex items-center text-green-600">
//               <CheckCircle className="h-5 w-5 mr-2" />
//               <span>This invoice has been paid. Thank you!</span>
//             </div>
//           ) : (
//             <>
//               {stripePromise ? (
//                 <Suspense fallback={
//                   <div className="flex items-center justify-center p-8">
//                     <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
//                     <span className="ml-2">Loading payment form...</span>
//                   </div>
//                 }>
//                   <Elements stripe={stripePromise}>
//                     <PaymentForm 
//                       invoice={invoice} 
//                       paymentId={paymentId}
//                       onPaymentSuccess={handlePaymentSuccess}
//                     />
//                   </Elements>
//                 </Suspense>
//               ) : (
//                 <div className="flex justify-center items-center p-8">
//                   <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
//                   <span className="ml-2">Loading payment system...</span>
//                 </div>
//               )}
//             </>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default InvoicePayment;